import { useCallback, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { Check, Circle, CircleHelp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  Button,
  Progress,
  Separator,
} from '@scio3/ui';
import { GET_APP_CFG } from '@scio3/graphql-client';
import { useTeamStore } from '@/stores/team-store';
import { YouTubeDialog } from '@/components/shared/YouTubeDialog';

interface GetStartedItem {
  text: string;
  subtext: string;
  button: string;
  url: string;
  type?: 'youtube' | 'link';
}

interface AppCfgResponse {
  getAppCfgs: {
    getStarts?: GetStartedItem[];
  } | null;
}

const STORAGE_KEY = 'scio3-get-started-completed';

function loadCompleted(): string[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

export function GetStartedDropdown() {
  const { t } = useTranslation();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const [youtubeUrl, setYoutubeUrl] = useState<string | null>(null);
  const [completedUrls, setCompletedUrls] = useState<string[]>(loadCompleted);

  const { data, loading } = useQuery<AppCfgResponse>(GET_APP_CFG, {
    variables: { teamId: activeTeamId, appType: 'scio' },
    skip: !activeTeamId,
  });

  const items: GetStartedItem[] = data?.getAppCfgs?.getStarts || [];

  const markCompleted = useCallback(
    (url: string) => {
      if (completedUrls.includes(url)) return;
      const updated = [...completedUrls, url];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      setCompletedUrls(updated);
    },
    [completedUrls],
  );

  const handleItemClick = (item: GetStartedItem) => {
    markCompleted(item.url);
    if (item.type === 'youtube') {
      setYoutubeUrl(item.url);
    }
  };

  const completedCount = items.filter((item) => completedUrls.includes(item.url)).length;
  const progressPercent = items.length > 0 ? Math.round((completedCount / items.length) * 100) : 0;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent"
            aria-label={t('Get Started')}
          >
            <CircleHelp className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-96 p-4">
          <div className="mb-1 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-primary">{t('Get Started')}</h2>
            {items.length > 0 && (
              <span className="text-sm text-muted-foreground">
                {completedCount}/{items.length}
              </span>
            )}
          </div>
          <p className="mb-2 text-sm text-muted-foreground">
            {t("Welcome to OptiSigns! Let's get you up and running in no time.")}
          </p>
          {items.length > 0 && <Progress value={progressPercent} className="mb-3 h-2" />}
          <Separator className="mb-3" />
          {loading && <p className="py-3 text-sm text-muted-foreground">{t('Loading...')}</p>}
          {!loading && items.length === 0 && (
            <p className="py-3 text-sm text-muted-foreground">{t('No data available')}</p>
          )}
          {/* Keys are dynamic from backend (getAppCfgs) — must match i18n files */}
          {items.map((item, i) => {
            const done = completedUrls.includes(item.url);
            return (
              <div
                key={item.url}
                className={cn(
                  'flex items-center gap-3 py-3',
                  i < items.length - 1 && 'border-b border-border',
                )}
              >
                {done ? (
                  <Check className="h-4 w-4 shrink-0 text-primary" />
                ) : (
                  <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <div className="flex-1">
                  <p className={cn('font-medium', done && 'text-muted-foreground line-through')}>
                    {t(item.text)}
                  </p>
                  <p className="text-sm text-muted-foreground">{t(item.subtext)}</p>
                </div>
                {item.type === 'youtube' ? (
                  <Button size="sm" onClick={() => handleItemClick(item)}>
                    {t(item.button)}
                  </Button>
                ) : (
                  <Button asChild size="sm" onClick={() => markCompleted(item.url)}>
                    <a href={item.url} target="_blank" rel="noopener noreferrer">
                      {t(item.button)}
                    </a>
                  </Button>
                )}
              </div>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <YouTubeDialog url={youtubeUrl} onClose={() => setYoutubeUrl(null)} />
    </>
  );
}
